import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { ProcurmentService } from 'src/app/services/procurment.service';
import { MaterialModule } from 'src/app/shared/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { TemplateMailComposeDialogComponent } from 'src/app/pages/mail/components/template-mail-compose-dialog/template-mail-compose-dialog.component';
import { EmailTemplateResolverService } from 'src/app/services/email-template-resolver.service';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-purchase-order-print',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './purchase-order-print.component.html',
  styleUrl: './purchase-order-print.component.scss'
})
export class PurchaseOrderPrintComponent implements OnInit, OnDestroy {
  orderId!: number;
  orderData: any;
  previewData: any = null;
  pdfDoc: any;
  pdfSrc: string | null = null;
  isDialogMode = false;
  includeSignature = false;
  isGenerating = false;
  private generatedBlobUrl: string | null = null;
  private rotatedImageCache = new Map<string, string>();
  private latestGenerationToken = 0;
  private pendingRegeneration = false;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';
  private readonly sessionData = JSON.parse(sessionStorage.getItem('tokenaccess') || localStorage.getItem('tokenaccess') || '{}');

  constructor(
    private route: ActivatedRoute,
    private procurementService: ProcurmentService,
    private emailTemplateResolver: EmailTemplateResolverService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<PurchaseOrderPrintComponent> | null
  ) { }

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    this.isDialogMode = !!this.dialogData?.id;
    this.orderId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (!this.orderId) {
      return;
    }
    this.loadPurchaseOrderData();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
    if (this.generatedBlobUrl) {
      URL.revokeObjectURL(this.generatedBlobUrl);
    }
  }

  loadPurchaseOrderData(): void {
    this.procurementService.getPurchaseOrderPDFbyid(this.orderId).subscribe({
      next: (data: any) => {
        this.orderData = Array.isArray(data) ? data[0] : data;
        this.rotatedImageCache.clear();
        void this.generatePdf();
      },
      error: (err) => console.error('Error loading purchase order', err)
    });
  }

  async generatePdf(): Promise<void> {
    if (!this.orderData) {
      return;
    }

    if (this.isGenerating) {
      this.pendingRegeneration = true;
      return;
    }

    this.isGenerating = true;
    this.pendingRegeneration = false;
    this.pdfSrc = null;
    this.previewData = null;
    const generationToken = ++this.latestGenerationToken;

    const b = this.orderData;
    const company = this.pickEntity(b, ['company', 'companymaster', 'companydetails']);
    const vendor = this.pickEntity(b, ['vendor', 'party', 'vendorMaster', 'partymaster', 'client']);
    const bank = this.pickEntity(b, ['bank', 'bankdetails', 'companybank']);
    const shipTo = this.pickEntity(b, ['shipto', 'deliveryaddress', 'branch', 'warehouse']);
    const items = this.extractItems(b);
    const logo = this.resolveImage([company.logoBase64, company.logobase64, b.logoBase64, b.logobase64]);
    const rawSignatureImage = this.resolveImage([
      b.signature,
      b.sign,
      b.default_signature_image_base64,
      b.signatureBase64,
      b.signaturebase64,
      b.authorizedSignatureBase64,
      b.authorizedsignaturebase64,
      company.signature,
      company.sign,
      company.default_signature_image_base64,
      company.signatureBase64,
      company.signaturebase64,
      company.authorizedSignatureBase64,
      company.authorizedsignaturebase64
    ]);
    const rawStampImage = this.resolveImage([
      b.stamp,
      b.companyStamp,
      b.default_stamp_image_base64,
      b.stampBase64,
      b.stampbase64,
      b.companyStampBase64,
      b.companystampbase64,
      company.stamp,
      company.companyStamp,
      company.default_stamp_image_base64,
      company.stampBase64,
      company.stampbase64,
      company.companyStampBase64,
      company.companystampbase64
    ]);

    const signSeed = this.orderId || Number(b.id || 0);
    const signBlockLayout = this.getSignBlockLayout(signSeed);
    const signatureImage = this.includeSignature && rawSignatureImage
      ? await this.rotateImageDataUrl(rawSignatureImage, signBlockLayout.signatureAngle)
      : null;
    const stampImage = this.includeSignature && rawStampImage
      ? await this.rotateImageDataUrl(rawStampImage, signBlockLayout.stampAngle)
      : null;

    const companyAddress = this.joinAddress([
      company.address1,
      company.address2,
      company.city,
      company.state,
      company.country,
      company.pincode
    ]);
    const vendorAddress = this.joinAddress([
      vendor.address1,
      vendor.address2,
      vendor.city,
      vendor.state,
      vendor.country,
      vendor.pincode
    ]);
    const deliveryAddress = this.firstMeaningfulText([
      b.deliveryaddress,
      b.deliveryAddress,
      this.joinAddress([
        shipTo.address1,
        shipTo.address2,
        shipTo.city,
        shipTo.state,
        shipTo.country,
        shipTo.pincode
      ])
    ]);
    const billingAddress = this.firstMeaningfulText([
      b.billingaddress,
      b.billingAddress,
      companyAddress
    ]);

    const poNo = this.safeValue(
      b.code || b.pono || b.purchaseno || b.purchaseorderno || b.orderno
    );
    const poDate = this.formatDateValue(b.podate || b.orderdate || b.createddate);
    const refNo = this.safeValue(b.partyrefno || b.refno || b.vendorrefno, '');
    const refDate = this.formatDateValue(b.partyrefdate || b.refdate);
    const paymentTerms = this.safeValue(b.paymentterms, '');
    const remarks = this.safeValue(b.remarks, '');
    const netAmount = Number(b.netbillamt || b.billamt || b.netamount || 0);
    const amountInWords = `${toWords.convert(netAmount, { currency: true })}/-`;
    const taxableAmount = Number(b.taxableamt || b.taxableamount || items.reduce((sum: number, item: any) => sum + Number(item.taxableamt || item.taxableamount || 0), 0));
    const taxAmount = Number(b.taxamt || b.taxamount || items.reduce((sum: number, item: any) => sum + Number(item.taxamt || item.taxamount || 0), 0));
    const poAmount = Number(b.billamt || b.poamount || taxableAmount + taxAmount);
    const signatoryName = this.includeSignature
      ? this.safeValue(
        b.authorized_signatory_name ||
        b.authorizedSignatoryName ||
        b.authorizedsignatoryname ||
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        company.authorizedsignatoryname ||
        'Authorized Signatory'
      )
      : 'Authorized Signatory';
    const signatoryTitle = this.includeSignature
      ? this.safeValue(
        b.authorized_signatory_title ||
        b.authorizedSignatoryTitle ||
        b.authorizedsignatorytitle ||
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        company.authorizedsignatorytitle ||
        '',
        ''
      )
      : '';

    const minimumRows = items.length <= 3 ? 4 : Math.min(6, items.length);
    const itemRows = items.map((item: any, index: number) => ([
      { text: String(index + 1), alignment: 'center' },
      {
        stack: [
          { text: this.safeValue(item.productname || item.description || item.itemname), margin: [0, 0, 0, 0] },
          ...(this.buildItemRemarkLines(item.remarks).map((text) => ({
            text,
            fontSize: 8,
            color: '#64748b',
            italics: true,
            margin: [0, 2, 0, 0]
          })))
        ],
        margin: [0, 1, 0, 1]
      },
      { text: this.safeValue(item.hsnsac || item.hsncode || item.hsn), alignment: 'center' },
      { text: this.safeValue(item.uomname || item.uom), alignment: 'center' },
      { text: this.formatNumber(item.quantity), alignment: 'right' },
      { text: this.formatCurrency(item.productrate || item.rate, true), alignment: 'right' },
      { text: this.formatCurrency(item.disamt || item.discountamount || 0, true), alignment: 'right' },
      { text: this.formatCurrency(item.taxableamount || item.taxableamt, true), alignment: 'right' },
      { text: this.formatCurrency(item.taxamount || item.taxamt, true), alignment: 'right' },
      { text: this.formatCurrency(item.netamount || item.netamt, true), alignment: 'right', bold: true }
    ]));

    const emptyRowsNeeded = Math.max(0, minimumRows - itemRows.length);
    for (let index = 0; index < emptyRowsNeeded; index++) {
      itemRows.push(new Array(10).fill({ text: ' ', fontSize: 9 }));
    }

    const tableBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'DESCRIPTION', style: 'tableHeader' },
        { text: 'HSN', style: 'tableHeader' },
        { text: 'UOM', style: 'tableHeader' },
        { text: 'QTY', style: 'tableHeader' },
        { text: 'RATE', style: 'tableHeader' },
        { text: 'DISCOUNT', style: 'tableHeader' },
        { text: 'TAXABLE', style: 'tableHeader' },
        { text: 'TAX', style: 'tableHeader' },
        { text: 'LINE TOTAL', style: 'tableHeader' }
      ],
      ...itemRows,
      [
        { text: 'GRAND TOTAL', colSpan: 9, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 3, 8, 3] },
        {}, {}, {}, {}, {}, {}, {}, {},
        { text: this.formatCurrency(netAmount, true), alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 3, 0, 3] }
      ]
    ];

    const summaryBody = [
      [
        { text: 'Taxable Amount', style: 'summaryLabel' },
        { text: this.formatCurrency(taxableAmount, true), style: 'summaryValue' }
      ],
      [
        { text: 'Tax Amount', style: 'summaryLabel' },
        { text: this.formatCurrency(taxAmount, true), style: 'summaryValue' }
      ],
      [
        { text: 'PO Amount', style: 'summaryLabel' },
        { text: this.formatCurrency(poAmount, true), style: 'summaryValue' }
      ],
      [
        { text: 'Rounded Off', style: 'summaryLabel' },
        { text: this.formatCurrency(b.rounoffamt || b.roundoffamt, true), style: 'summaryValue' }
      ],
      [
        { text: 'Net Amount', style: 'summaryTotalLabel' },
        { text: this.formatCurrency(netAmount, true), style: 'summaryTotalValue' }
      ]
    ];

    this.previewData = {
      logo,
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name).toUpperCase(),
      companyNameTitle: this.safeValue(company.name),
      companyAddress,
      companyMeta: this.buildCompanyMeta(company),
      poNo,
      poDate,
      status: this.safeValue(b.docstatusname || b.docstatus || b.status, ''),
      vendorState: this.safeValue(vendor.state, ''),
      vendorName: this.safeValue(vendor.name || vendor.partyname || vendor.vendorname),
      vendorAddress,
      vendorMeta: [
        vendor.code ? `Code: ${vendor.code}` : '',
        vendor.mobile ? `Contact: ${vendor.mobile}` : '',
        vendor.email ? `Email: ${vendor.email}` : '',
        vendor.gstin ? `GSTIN: ${vendor.gstin}` : '',
        vendor.state ? `State: ${vendor.state}` : ''
      ].filter(Boolean),
      billingName: this.safeValue(
        b.billingaddressname || b.billingAddressName || company.name || 'Billing Address',
        'Billing Address'
      ),
      billingAddress,
      billingMeta: [
        company.gstin ? `GSTIN: ${company.gstin}` : '',
        company.email ? `Email: ${company.email}` : '',
        company.website ? `Website: ${company.website}` : ''
      ].filter(Boolean),
      deliveryName: this.safeValue(shipTo.name || shipTo.branchname || shipTo.whname, ''),
      deliveryAddress,
      deliveryMeta: [
        b.contactperson ? `Contact Person: ${b.contactperson}` : '',
        b.contactphone ? `Contact Phone: ${b.contactphone}` : '',
        refNo ? `Party Ref No: ${refNo}` : '',
        refDate !== '-' ? `Party Ref Date: ${refDate}` : '',
        b.loadingunloading ? 'Loading / Unloading: Included' : '',
        b.transporation ? 'Transportation: Included' : ''
      ].filter(Boolean),
      items: items.map((item: any, index: number) => ({
        sr: String(index + 1),
        description: this.safeValue(item.productname || item.description || item.itemname),
        remarkLines: this.buildItemRemarkLines(item.remarks),
        hsn: this.safeValue(item.hsnsac || item.hsncode || item.hsn),
        uom: this.safeValue(item.uomname || item.uom),
        qty: this.formatNumber(item.quantity),
        rate: this.formatCurrency(item.productrate || item.rate, true),
        discount: this.formatCurrency(item.disamt || item.discountamount || 0, true),
        taxable: this.formatCurrency(item.taxableamount || item.taxableamt, true),
        tax: this.formatCurrency(item.taxamount || item.taxamt, true),
        lineTotal: this.formatCurrency(item.netamount || item.netamt, true)
      })),
      netAmount: this.formatCurrency(netAmount, true),
      bankDetails: [
        this.metaLine('Bank', bank.bankname),
        this.metaLine('Branch', bank.branch),
        this.metaLine('A/C No', bank.accountno),
        this.metaLine('IFSC', bank.ifsc)
      ].filter(Boolean),
      amountInWords,
      paymentTerms,
      remarks,
      summary: [
        { label: 'Taxable Amount', value: this.formatCurrency(taxableAmount, true) },
        { label: 'Tax Amount', value: this.formatCurrency(taxAmount, true) },
        { label: 'PO Amount', value: this.formatCurrency(poAmount, true) },
        { label: 'Rounded Off', value: this.formatCurrency(b.rounoffamt || b.roundoffamt, true) },
        { label: 'Net Amount', value: this.formatCurrency(netAmount, true) }
      ],
      stampImage,
      signatureImage,
      signatoryName,
      signatoryTitle
    };

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 10, 20, 44],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              {
                width: '*',
                text: 'Generated using VCS ERP',
                style: 'footerText'
              },
              {
                width: 90,
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerPageText',
                alignment: 'right'
              }
            ]
          }
        ]
      }),
      background: logo
        ? { image: logo, alignment: 'center', width: 300, height: 300, opacity: 0.055, marginTop: 220 }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [74, '*', 150],
            body: [[
              {
                stack: logo
                  ? [{ image: logo, width: 66, fit: [66, 66], alignment: 'center' }]
                  : [{ text: this.getInitials(company.name), style: 'logoFallback' }],
                border: [false, false, false, false],
                margin: [0, 6, 0, 0]
              },
              {
                stack: [
                  { text: this.safeValue(company.name).toUpperCase(), style: 'companyHeader' },
                  companyAddress ? { text: companyAddress, style: 'companyMeta' } : '',
                  ...this.buildCompanyMeta(company).map((text) => ({ text, style: 'companyMeta' }))
                ].filter(Boolean),
                border: [false, false, false, false],
                margin: [0, 4, 0, 0]
              },
              {
                stack: [
                  { text: 'PURCHASE ORDER', style: 'invoiceHeaderBadge' },
                  { text: `PO No: ${poNo}`, style: 'invoiceMetaStrong' },
                  { text: `PO Date: ${poDate}`, style: 'invoiceMeta' },
                  this.previewData.status ? { text: `Status: ${this.previewData.status}`, style: 'invoiceMeta' } : '',
                  refNo ? { text: `Ref No: ${refNo}`, style: 'invoiceMeta' } : ''
                ].filter(Boolean),
                fillColor: '#f6f8ff',
                margin: [10, 8, 10, 8],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: [
              {
                stack: [
                { text: 'Supplier Information', style: 'cardTitle' },
                  { text: this.previewData.vendorName, style: 'cardHeading' },
                  vendorAddress ? { text: vendorAddress, style: 'cardText' } : '',
                  ...this.previewData.vendorMeta.map((text: string) => ({ text, style: 'cardText' }))
                ].filter(Boolean),
                margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            },
              {
                stack: [
                { text: 'Bill To', style: 'cardTitle' },
                  this.previewData.billingName ? { text: this.previewData.billingName, style: 'cardHeading' } : '',
                  billingAddress ? { text: billingAddress, style: 'cardText' } : '',
                  ...this.previewData.billingMeta.map((text: string) => ({ text, style: 'cardText' }))
                ].filter(Boolean),
                margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            },
              {
                stack: [
                { text: 'Ship To', style: 'cardTitle' },
                  this.previewData.deliveryName ? { text: this.previewData.deliveryName, style: 'cardHeading' } : '',
                  deliveryAddress ? { text: deliveryAddress, style: 'cardText' } : '',
                  ...this.previewData.deliveryMeta.map((text: string) => ({ text, style: 'cardText' }))
                ].filter(Boolean),
                margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            }
          ],
          columnGap: 14,
          margin: [0, 6, 0, 4]
        },
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 38, 32, 32, 48, 48, 54, 42, 54],
            body: tableBody
          },
          layout: {
            fillColor: (rowIndex: number, node: any) => {
              if (rowIndex === 0) {
                return '#0f172a';
              }
              if (rowIndex === node.table.body.length - 1) {
                return '#eef2ff';
              }
              return rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff';
            },
            hLineColor: (rowIndex: number) => rowIndex === 0 ? '#1e293b' : '#93a4bb',
            vLineColor: () => '#a7b5c8',
            hLineWidth: (rowIndex: number, node: any) =>
              rowIndex === 0 || rowIndex === 1 || rowIndex === node.table.body.length ? 1.1 : 0.8,
            vLineWidth: () => 0.8,
            paddingTop: () => 5,
            paddingBottom: () => 5,
            paddingLeft: () => 5,
            paddingRight: () => 5
          }
        },
        {
          unbreakable: true,
          columns: [
            {
              width: '*',
              stack: [
                ...(this.previewData.bankDetails.length
                  ? [{
                    stack: [
                      { text: 'Bank Details', style: 'sectionHeader' },
                      ...this.previewData.bankDetails.map((text: string) => ({ text, style: 'detailText' }))
                    ],
                    fillColor: '#f8fafc',
                    margin: [7, 7, 7, 7]
                  }]
                  : []),
                {
                  stack: [
                    { text: 'Amount In Words', style: 'sectionHeader' },
                    { text: amountInWords, style: 'amountWords' }
                  ],
                  fillColor: '#f8fafc',
                  margin: [0, this.previewData.bankDetails.length ? 6 : 0, 0, 0]
                },
                ...(remarks
                  ? [{
                    stack: [
                      { text: 'Remarks', style: 'sectionHeader' },
                      { text: remarks, style: 'detailText' }
                    ],
                    fillColor: '#f8fafc',
                    margin: [0, 6, 0, 0]
                  }]
                  : []),
                ...(paymentTerms
                  ? [{
                    stack: [
                      { text: 'Commercial Terms', style: 'sectionHeader' },
                      { text: paymentTerms, style: 'detailText' }
                    ],
                    fillColor: '#f8fafc',
                    margin: [0, 6, 0, 0]
                  }]
                  : [])
              ]
            },
            {
              width: 188,
              stack: [
                {
                  table: {
                    widths: ['*', 'auto'],
                    body: summaryBody
                  },
                  layout: {
                    hLineColor: () => '#93a4bb',
                    vLineColor: () => '#a7b5c8',
                    hLineWidth: () => 0.9,
                    vLineWidth: () => 0.9,
                    paddingTop: () => 5,
                    paddingBottom: () => 5,
                    paddingLeft: () => 6,
                    paddingRight: () => 6
                  }
                },
                {
                  stack: [
                    {
                      text: `For ${this.safeValue(company.name)}`,
                      style: 'signatoryCompanyInline',
                      alignment: 'right',
                      margin: [0, 0, 0, 4]
                    },
                    {
                      columns: [
                        { width: '*', text: '' },
                        {
                          width: 70,
                          stack: [
                            stampImage
                              ? { image: stampImage, fit: signBlockLayout.stampFit, opacity: signBlockLayout.stampOpacity, alignment: 'right', margin: signBlockLayout.stampMargin }
                              : { text: ' ', margin: [0, 0, 0, 20] }
                          ]
                        },
                        {
                          width: 118,
                          stack: [
                            signatureImage
                              ? { image: signatureImage, fit: signBlockLayout.signatureFit, alignment: 'right', margin: signBlockLayout.signatureMargin }
                              : { text: ' ', margin: [0, 4, 0, 20] }
                          ]
                        }
                      ],
                      columnGap: -16,
                      margin: [0, 0, 0, 0]
                    },
                    {
                      text: signatoryName,
                      style: 'signatoryName',
                      alignment: 'right',
                      margin: [0, this.includeSignature ? 0 : 12, 0, 0]
                    },
                    signatoryTitle
                      ? {
                        text: signatoryTitle,
                        style: 'signatoryRole',
                        alignment: 'right'
                      }
                      : ''
                  ],
                  margin: [0, 8, 0, 0]
                }
              ]
            }
          ],
          columnGap: 12,
          margin: [0, 4, 0, 0]
        }
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#0f172a', alignment: 'center', margin: [0, 18, 0, 0] },
        companyHeader: { fontSize: 17, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.3, color: '#3f526d', margin: [0, 1, 0, 0], alignment: 'center' },
        invoiceHeaderBadge: { fontSize: 14, bold: true, alignment: 'center', color: '#11284c', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.8, bold: true, color: '#11284c', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 8.8, color: '#43546b', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 8.2, bold: true, color: '#1d4ed8', margin: [0, 0, 0, 4] },
        cardHeading: { fontSize: 10.8, bold: true, color: '#11284c', margin: [0, 0, 0, 3] },
        cardText: { fontSize: 8.7, color: '#24364e', margin: [0, 1, 0, 0] },
        sectionHeader: { fontSize: 9, bold: true, color: '#11284c', margin: [0, 0, 0, 4] },
        tableHeader: { bold: true, fontSize: 7.6, color: '#ffffff', alignment: 'center' },
        detailText: { fontSize: 8.6, color: '#24364e', margin: [0, 1, 0, 0] },
        amountWords: { fontSize: 9.1, color: '#11284c', bold: true },
        summaryLabel: { fontSize: 8.5, color: '#43546b', alignment: 'right' },
        summaryValue: { fontSize: 8.8, color: '#11284c', alignment: 'right', bold: true },
        summaryTotalLabel: { fontSize: 9.2, color: '#11284c', alignment: 'right', bold: true },
        summaryTotalValue: { fontSize: 11.6, color: '#c2410c', alignment: 'right', bold: true },
        signatoryCompanyInline: { fontSize: 8.3, bold: true, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c', margin: [0, 0, 0, 0] },
        signatoryRole: { fontSize: 8.5, color: '#475569', margin: [0, 1, 0, 0] },
        footerText: { fontSize: 6.8, color: '#64748b', margin: [0, 0, 0, 0] },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
    this.pdfDoc.getBlob((blob: Blob) => {
      const newBlobUrl = URL.createObjectURL(blob);

      if (generationToken !== this.latestGenerationToken) {
        URL.revokeObjectURL(newBlobUrl);
        return;
      }

      if (this.generatedBlobUrl) {
        URL.revokeObjectURL(this.generatedBlobUrl);
      }

      this.generatedBlobUrl = newBlobUrl;
      this.pdfSrc = `${this.generatedBlobUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=100`;
      this.isGenerating = false;

      if (this.pendingRegeneration) {
        void this.generatePdf();
      }
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }
    this.includeSignature = checked;
    void this.generatePdf();
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Purchase-Order-${this.previewData?.poNo || this.orderId}.pdf`);
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private pickEntity(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value && typeof value === 'object') {
        return Array.isArray(value) ? value[0] || {} : value;
      }
    }
    return {};
  }

  private extractItems(source: any): any[] {
    const candidates = [
      source?.items,
      source?.purchaseorderitemdetails,
      source?.purchaseOrderItemDetails,
      source?.purchaseorderdetails,
      source?.details
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('MSME', company.msmeNo || company.msmeno),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website)
    ].filter(Boolean) as string[];
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    if (!normalized || ['-', 'n/a', 'na', 'null', 'undefined'].includes(normalized.toLowerCase())) {
      return '';
    }

    return `${label}: ${normalized}`;
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private getSignBlockLayout(seedBase: number) {
    const seed = Math.abs(seedBase || 1) * 97;
    const next = (min: number, max: number, step = 1, salt = 0) =>
      this.seededValue(seed + salt, min, max, step);

    return {
      stampFit: [next(66, 74, 1, 11), next(66, 74, 1, 17)] as [number, number],
      stampMargin: [next(8, 14, 1, 3), next(5, 11, 1, 29), next(-3, 1, 1, 31), next(-8, -2, 1, 37)] as [number, number, number, number],
      stampOpacity: 0.68,
      stampAngle: next(-4, 4, 1, 41),
      signatureFit: [next(108, 120, 1, 43), next(34, 42, 1, 47)] as [number, number],
      signatureMargin: [next(-8, 2, 1, 53), next(0, 7, 1, 59), 0, next(-1, 4, 1, 61)] as [number, number, number, number],
      signatureAngle: next(-3, 3, 1, 67)
    };
  }

  private seededValue(seed: number, min: number, max: number, step = 1): number {
    const spread = Math.floor((max - min) / step) + 1;
    const raw = Math.abs(Math.sin(seed) * 10000);
    const offset = Math.floor(raw % spread);
    return min + offset * step;
  }

  private rotateImageDataUrl(dataUrl: string, angle: number): Promise<string> {
    return new Promise((resolve) => {
      const cacheKey = `${angle}::${dataUrl.slice(0, 120)}`;
      const cached = this.rotatedImageCache.get(cacheKey);
      if (cached) {
        resolve(cached);
        return;
      }

      if (!angle) {
        resolve(dataUrl);
        return;
      }

      const image = new Image();
      image.onload = () => {
        const radians = (angle * Math.PI) / 180;
        const sin = Math.abs(Math.sin(radians));
        const cos = Math.abs(Math.cos(radians));
        const width = image.width;
        const height = image.height;
        const rotatedWidth = Math.ceil(width * cos + height * sin);
        const rotatedHeight = Math.ceil(width * sin + height * cos);

        const canvas = document.createElement('canvas');
        canvas.width = rotatedWidth;
        canvas.height = rotatedHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }

        ctx.clearRect(0, 0, rotatedWidth, rotatedHeight);
        ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
        ctx.rotate(radians);
        ctx.drawImage(image, -width / 2, -height / 2, width, height);
        const rotated = canvas.toDataURL('image/png');
        this.rotatedImageCache.set(cacheKey, rotated);
        resolve(rotated);
      };

      image.onerror = () => resolve(dataUrl);
      image.src = dataUrl;
    });
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.normalizePart(part))
      .filter(Boolean)
      .join(', ');
  }

  private firstMeaningfulText(candidates: any[]): string {
    for (const candidate of candidates) {
      const normalized = this.normalizePart(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return '';
  }

  private buildItemRemarkLines(value: any): string[] {
    const normalized = this.normalizePart(value);
    if (!normalized) {
      return [];
    }

    return [`[${normalized}]`];
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private formatCurrency(value: any, withSymbol = false): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return withSymbol ? `${String.fromCharCode(8377)} ${formatted}` : formatted;
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'PO');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  async sendMail() {
    if (!this.previewData || !this.pdfDoc) {
      this.snackBar.open('Purchase order preview is still loading. Please try again in a moment.', 'Close', { duration: 3000 });
      return;
    }

    if (this.isGenerating) {
      this.snackBar.open('PDF generation is in progress. Please wait a moment.', 'Close', { duration: 3000 });
      return;
    }

    try {
      const resolved = await firstValueFrom(
        this.emailTemplateResolver.resolvePurchaseOrderMailDraft(2222, this.orderId)
      );
      const attachment = await this.buildMailAttachment();
      const draft = {
        ...resolved.draft,
        companyid: Number(this.sessionData?.companyId || 0),
        userid: Number(this.decodeSessionUserId() || 0),
        files: [...(resolved.draft.files || []), attachment]
      };
      this.dialog.open(TemplateMailComposeDialogComponent, {
        width: 'auto',
        maxWidth: 'calc(100vw - 12px)',
        panelClass: 'mail-compose-dialog-panel',
        autoFocus: false,
        data: {
          account: null,
          draft
        }
      });
    } catch (error) {
      console.error('Failed to prepare purchase order mail', error);
      this.snackBar.open('Unable to prepare purchase order email draft right now.', 'Close', { duration: 3500 });
    }
  }

    private async buildMailAttachment(): Promise<File> {
    const blob = await new Promise<Blob>((resolve, reject) => {
      if (!this.pdfDoc) {
        reject(new Error('PDF document is not ready.'));
        return;
      }

      this.pdfDoc.getBlob((pdfBlob: Blob) => resolve(pdfBlob));
    });

    const docNo = this.safeValue(this.orderData?.pono, String(this.orderId)).replace(/[^\w.-]+/g, '-');
    return new File([blob], `Purchase-Order-${docNo}.pdf`, { type: 'application/pdf' });
  }

  private decodeSessionUserId(): number {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return Number(payload?.unique_name || payload?.nameid || this.sessionData?.userId || this.sessionData?.userid || 0);
    } catch {
      return Number(this.sessionData?.userId || this.sessionData?.userid || 0);
    }
  }

}
